import { useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useExpenses } from "../../../lib/expenses/useExpenses";
import { Card, EmptyState, formatCents } from "./shared";

// CategoryTrend — stacked-area chart of expense spend by category across
// the last 12 full months. Keeps the top-6 categories by total spend and
// rolls everything else into "Other" so the legend stays readable.
//
// Tooltip formatter renders each category on its own line so the hovered
// month's breakdown is legible. Colors cycle through a fixed palette that
// mirrors the Tailwind -500 tokens used elsewhere.

const PALETTE = [
  "#6366F1", // indigo-500
  "#10B981", // emerald-500
  "#F59E0B", // amber-500
  "#F43F5E", // rose-500
  "#06B6D4", // cyan-500
  "#8B5CF6", // violet-500
  "#94A3B8", // slate-400 — reserved for "Other"
];

const TOP_N = 6;

function last12Months(now: Date = new Date()) {
  const out: Array<{ key: string; label: string }> = [];
  const anchor = new Date(now.getFullYear(), now.getMonth(), 1);
  for (let i = 11; i >= 0; i--) {
    const d = new Date(anchor);
    d.setMonth(d.getMonth() - i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const label = d.toLocaleString("en-US", {
      month: "short",
      year: "2-digit",
    });
    out.push({ key: `${y}-${m}`, label });
  }
  return out;
}

export function CategoryTrend({
  submittedBy,
}: { submittedBy?: string | null } = {}) {
  const { expenses } = useExpenses();

  const { rows, series } = useMemo(() => {
    const months = last12Months();
    const monthKeys = new Set(months.map((m) => m.key));

    // Pass 1: total by category so we can pick the top N.
    const totals = new Map<string, number>();
    for (const e of expenses) {
      if (submittedBy && e.submittedBy !== submittedBy) continue;
      const key = e.occurredOn.slice(0, 7);
      if (!monthKeys.has(key)) continue;
      const label = e.categoryLabel?.trim() || "Uncategorized";
      totals.set(label, (totals.get(label) ?? 0) + e.amountCents);
    }
    const sorted = Array.from(totals.entries()).sort((a, b) => b[1] - a[1]);
    const topCategories = sorted.slice(0, TOP_N).map(([label]) => label);
    const hasOther = sorted.length > TOP_N;
    const visibleSeries: string[] = [...topCategories];
    if (hasOther) visibleSeries.push("Other");

    // Pass 2: build per-month buckets for each visible series.
    const buckets: Map<string, Map<string, number>> = new Map(
      months.map((m) => [m.key, new Map(visibleSeries.map((s) => [s, 0]))]),
    );
    const topSet = new Set(topCategories);
    for (const e of expenses) {
      if (submittedBy && e.submittedBy !== submittedBy) continue;
      const key = e.occurredOn.slice(0, 7);
      const monthBucket = buckets.get(key);
      if (!monthBucket) continue;
      const label = e.categoryLabel?.trim() || "Uncategorized";
      const bucketName = topSet.has(label) ? label : hasOther ? "Other" : label;
      if (!monthBucket.has(bucketName)) continue;
      monthBucket.set(
        bucketName,
        (monthBucket.get(bucketName) ?? 0) + e.amountCents,
      );
    }

    const chartRows = months.map((m) => {
      const row: Record<string, string | number> = { month: m.label };
      const b = buckets.get(m.key);
      if (b) {
        for (const [name, value] of b.entries()) {
          row[name] = value;
        }
      }
      return row;
    });

    return { rows: chartRows, series: visibleSeries };
  }, [expenses, submittedBy]);

  const hasAnySpend = rows.some((r) =>
    series.some((s) => typeof r[s] === "number" && (r[s] as number) > 0),
  );

  return (
    <Card
      title="Category trend (last 12 months)"
      testId="report-category-trend"
    >
      {!hasAnySpend ? (
        <EmptyState message="No expenses in the last 12 months yet." />
      ) : (
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={rows}
              margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
            >
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: "#64748b" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tickFormatter={(v) => formatCents(Number(v))}
                tick={{ fontSize: 11, fill: "#64748b" }}
                axisLine={false}
                tickLine={false}
                width={80}
              />
              <Tooltip
                formatter={(v: number, name: string) => [formatCents(v), name]}
              />
              <Legend
                verticalAlign="top"
                height={28}
                wrapperStyle={{ fontSize: "11px" }}
              />
              {series.map((name, i) => (
                <Area
                  key={name}
                  type="monotone"
                  dataKey={name}
                  name={name}
                  stackId="1"
                  stroke={PALETTE[i % PALETTE.length]}
                  fill={PALETTE[i % PALETTE.length]}
                  fillOpacity={0.6}
                  isAnimationActive={false}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
